import { Icon, OptionSelector, OptionSelectorOption, ProgressBadge, Tooltip, useToast } from '@aidkitorg/component-library';
import { TargetFieldCategoryValue } from '@aidkitorg/types/lib/admin-panel/settings';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useReports } from '../../../Contexts/ReportsContext';
import { useSettings } from '../../../Contexts/SettingsContext';
import { useLocalizedStrings } from '../../../Localization';
import { CATEGORY_OPTIONS } from '../../Settings/utils';
import { ReportConfig } from '@aidkitorg/aidkit/lib/admin_panel/reports/domain';
import { FieldExamplesTooltip } from '../../Settings/components/FieldExamplesTooltip';

const MAXIMUM_FIELD_COUNT = 40;
const SYNTHETIC_UID_FIELD = "__synthetic_uid__";
const SYNTHETIC_FILTERED_FIELD = "__filtered__"
const SYNTHETIC_VIEW_APPLICANTS_FIELD = "__synthetic_view_applicants__";

interface ReportsBuilderPanelProps {
  isEditMode: boolean;
}

export const ReportsBuilderPanel = ({ isEditMode }: ReportsBuilderPanelProps) => {
  const L = useLocalizedStrings();
  const { stagedConfig, setStagedConfig } = useReports();
  const { fields, isFieldsLoading } = useSettings();
  const { toast } = useToast();
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const [lastStagedConfigId, setLastStagedConfigId] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  // Fields are now auto-loaded by SettingsContext - no manual API calls needed

  // Initialize new reports with synthetic UID field selected (only once)
  useEffect(() => {
    if (!isEditMode && fields.length > 0 && selectedFields.length === 0 && !stagedConfig && !hasInitialized) {
      const syntheticUidField = fields.find(field => field.id === SYNTHETIC_UID_FIELD);
      if (syntheticUidField) {
        setSelectedFields([SYNTHETIC_UID_FIELD]);
        setHasInitialized(true);
      }
    }
  }, [isEditMode, fields, selectedFields, stagedConfig, hasInitialized]);


  // Populate form fields when stagedConfig changes (e.g., in edit mode)
  useEffect(() => {
    if (stagedConfig) {
      // Create a unique identifier for this config to avoid re-processing the same one
      const configId = JSON.stringify(stagedConfig);

      // Only update if this is a different config than what we last processed
      if (configId !== lastStagedConfigId) {
        setLastStagedConfigId(configId);

        // Extract field IDs from the config columns
        const fieldIds = stagedConfig.columns
          .filter((col): col is { kind: 'Field', field: string } => col.kind === 'Field')
          .map(col => col.field);
        setSelectedFields(fieldIds);
      }
    } else if (stagedConfig === null && lastStagedConfigId !== null) {
      // Reset when stagedConfig is explicitly set to null
      setLastStagedConfigId(null);
      setSelectedFields([]);
    }
  }, [stagedConfig, lastStagedConfigId]);

  const toggleParentExpansion = useCallback((parentId: string) => {
    setExpandedParents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(parentId)) {
        newSet.delete(parentId);
      } else {
        newSet.add(parentId);
      }
      return newSet;
    });
  }, []);

  const handleDerivedFieldChange = useCallback((fieldId: string, checked: boolean) => {
    if (checked) {
      setSelectedFields(prev => {
        const totalFieldCount = prev.length;
        if (totalFieldCount >= MAXIMUM_FIELD_COUNT) {
          toast({
            title: L.admin_panel.reports.field_limit_reached,
            description: L.admin_panel.reports.field_limit_warning,
            variant: "error",
          });
          return prev;
        }
        return [...prev, fieldId];
      });
    } else {
      setSelectedFields(prev => prev.filter(id => id !== fieldId));
    }
  }, [toast, L]);

  // Extract field IDs from current filters to suggest as columns
  const filterFieldIds = useMemo(() => {
    if (!stagedConfig?.filter) return new Set<string>();
    
    const extractFieldsFromFilter = (obj: any): Set<string> => {
      const fieldIds = new Set<string>();
      
      // Recursively traverse the object to find all field references
      const traverse = (item: any) => {
        if (!item || typeof item !== 'object') return;
        
        // Pattern 1: Object with kind: 'Field' and field property
        if (item.kind === 'Field' && typeof item.field === 'string') {
          fieldIds.add(item.field);
          
          
          return;
        }
        
        // Pattern 2: Filter objects that directly have a field property (like "Exists" filters)
        if (typeof item.field === 'string' && item.field !== '') {
          fieldIds.add(item.field);
          
        }
        
        // Recursively traverse all properties of this object
        for (const key in item) {
          if (item.hasOwnProperty(key)) {
            const value = item[key];
            if (Array.isArray(value)) {
              // Traverse array elements
              value.forEach(traverse);
            } else if (typeof value === 'object' && value !== null) {
              // Traverse nested objects
              traverse(value);
            }
          }
        }
      };
      
      traverse(obj);
      return fieldIds;
    };
    
    return extractFieldsFromFilter(stagedConfig.filter);
  }, [stagedConfig?.filter]);

  // Convert fields to OptionSelector format with derived fields nested under parents
  const fieldOptions: OptionSelectorOption[] = useMemo(() => {
    // NOTE: The 'fields' array should already be filtered by user permissions
    // from the SettingsContext.getFields() call, which respects user access rights

    // Separate parent and derived fields
    const parentFields = fields.filter(field => !('parent' in field));
    const derivedFields = fields.filter(field => 'parent' in field);

    // Group derived fields by their parent (only include accessible derived fields)
    const derivedByParent = derivedFields.reduce((acc, derivedField) => {
      const parentId = (derivedField as any).parent;
      if (!acc[parentId]) {
        acc[parentId] = [];
      }
      acc[parentId].push(derivedField);
      return acc;
    }, {} as Record<string, any[]>);

    // Create options for parent fields with nested derived fields
    const regularFieldOptions = parentFields.map(field => {
      const baseLabel = field.displayName || field.id;
      const childFields = derivedByParent[field.id] || [];

      // Get category
      let category: { value: string, label: string } | undefined;
      if ('category' in field && field.category) {
        category = {
          value: field.category as TargetFieldCategoryValue,
          label: CATEGORY_OPTIONS.find(cat => cat.value === field.category)?.label || field.category
        };
      }

      // Create description with field ID and description
      const description = (
        <div className="flex flex-col gap-1">
          {('description' in field && field.description) && (
            <span className="text-gray-600 text-xs font-medium">{field.description}</span>
          )}
          <div className="flex items-center gap-1">
            <code className="self-start bg-gray-100 border border-gray-200 font-mono inline-block px-1.5 py-0.5 rounded text-gray-800 text-xs">
              {field.id === SYNTHETIC_UID_FIELD ? "uid" : field.id}
            </code>
            <FieldExamplesTooltip fieldName={field.id === SYNTHETIC_UID_FIELD ? "uid" : field.id} />
          </div>
        </div>
      );

      // Create sub-options for derived fields if any exist
      const isExpanded = expandedParents.has(field.id);
      const subOptions = childFields.length > 0 ? {
        title: L.admin_panel.reports.related_fields,
        expanded: isExpanded,
        onToggleExpanded: () => toggleParentExpansion(field.id),
        options: childFields.map(derivedField => {
          const labelText = derivedField.displayName || derivedField.id;
          const isSelected = selectedFields.includes(derivedField.id);
          const totalFieldCount = selectedFields.length;
          const isAtLimit = totalFieldCount >= MAXIMUM_FIELD_COUNT;
          return {
            id: derivedField.id,
            label: (
              <div className="flex items-center gap-1">
                <code className="text-xs bg-gray-100 border border-gray-200 font-mono whitespace-pre-wrap m-0 px-0.5 py-0.5 text-gray-800 rounded inline-block">{labelText}</code>
                <FieldExamplesTooltip fieldName={derivedField.id} />
              </div>
            ),
            checked: isSelected,
            disabled: !isSelected && isAtLimit,
            onChange: (checked: boolean) => handleDerivedFieldChange(derivedField.id, checked)
          };
        })
      } : undefined;

      const isSelected = selectedFields.includes(field.id);
      const totalFieldCount = selectedFields.length;
      const isAtLimit = totalFieldCount >= MAXIMUM_FIELD_COUNT;

      return {
        value: field.id,
        label: baseLabel,
        category,
        description,
        disabled: !isSelected && isAtLimit,
        subOptions
      };
    });

    // Create persistent filtered fields section showing all filter fields with their current state
    const filteredFieldOptions: OptionSelectorOption[] = [];
    
    if (filterFieldIds.size > 0) {
      // Show ALL filter fields (whether selected or not) in the filtered fields section
      Array.from(filterFieldIds).forEach(fieldId => {
        const field = fields.find(f => f.id === fieldId);
        
        if (field) {
          const baseLabel = field.displayName || field.id;
          const isSelected = selectedFields.includes(fieldId);
          const totalFieldCount = selectedFields.length;
          const isAtLimit = totalFieldCount >= MAXIMUM_FIELD_COUNT;

          // Create description without filter indicator (moved to category level)
          const description = (
            <div className="flex flex-col gap-1">
              {('description' in field && field.description) && (
                <span className="text-gray-600 text-xs font-medium">{field.description}</span>
              )}
              <div className="flex items-center gap-1">
                <code className="self-start bg-gray-100 border border-gray-200 font-mono inline-block px-1.5 py-0.5 rounded text-gray-800 text-xs">
                  {field.id === SYNTHETIC_UID_FIELD ? "uid" : field.id}
                </code>
                <FieldExamplesTooltip fieldName={field.id === SYNTHETIC_UID_FIELD ? "uid" : field.id} />
              </div>
            </div>
          );

          filteredFieldOptions.push({
            value: `${SYNTHETIC_FILTERED_FIELD}${field.id}`, // Use a prefixed value to avoid conflicts
            label: baseLabel,
            category: { 
              value: SYNTHETIC_FILTERED_FIELD, 
              label: L.admin_panel.reports.suggested 
            },
            description
          });
        }
      });
    }

    // Create synthetic "View Applicants" field
    const syntheticViewApplicantsField: OptionSelectorOption = {
      value: SYNTHETIC_VIEW_APPLICANTS_FIELD,
      label: 'View Applicants',
      category: {
        value: 'action',
        label: 'Actions'
      },
      description: (
        <div className="flex flex-col gap-1">
          <span className="text-gray-600 text-xs font-medium">Displays a link to view applicant details</span>
        </div>
      ),
    };

    // Combine filtered fields with synthetic fields and regular fields
    return [...filteredFieldOptions, syntheticViewApplicantsField, ...regularFieldOptions];
  }, [fields, selectedFields, expandedParents, toggleParentExpansion, handleDerivedFieldChange, filterFieldIds, L]);

  // Convert selected fields to include prefixed filtered field IDs for OptionSelector
  const selectedValuesForOptionSelector = useMemo(() => {
    const result: string[] = [];
    
    // Add all regular selected fields
    selectedFields.forEach(fieldId => {
      result.push(fieldId);
      
      // If this field is also in filters, add its prefixed version too
      if (filterFieldIds.has(fieldId)) {
        result.push(`${SYNTHETIC_FILTERED_FIELD}${fieldId}`);
      }
    });
    
    return result;
  }, [selectedFields, filterFieldIds]);

  // Update stagedConfig when selectedFields changes
  useEffect(() => {
    if (selectedFields.length > 0) {
      const columns = selectedFields.map(fieldId => {
        // Create a Field expression for each selected field
        return {
          kind: 'Field' as const,
          field: fieldId
        };
      });

      const config: ReportConfig = {
        columns,
        // Preserve existing filter instead of always setting "Not Never"
        filter: stagedConfig?.filter || undefined,
      };

      // Only update if the new config is different from the current one
      const newConfigId = JSON.stringify(config);
      if (newConfigId !== lastStagedConfigId) {
        setStagedConfig(config);
      }
    } else {
      // Only set to null if we're not already null
      if (lastStagedConfigId !== null) {
        setStagedConfig(stagedConfig?.filter ? { columns: [], filter: stagedConfig.filter } : null);
      }
    }
  }, [selectedFields, setStagedConfig, lastStagedConfigId]);

  // Filtered fields are the "suggested" fields that will show up first in the Option Selector
  // These are fields that are used in the filter config, so we bubble them up here as they are likely
  // candidates for the columns a user will want to see in the table.
  // The checkboxes in the "suggested" section are a proxy to the actual field selection (a given field will show up
  // twice: once in the suggested category and once in the proper category). 
  // The logic around the synthetic __filtered__ key is to allow us to keep both of the checkboxes
  // in sync.
  const getOptionSelectorProps = useCallback(() => {
    const hasFilteredFields = filterFieldIds.size > 0;
    
    const handleSelectionChange = hasFilteredFields 
      ? (newSelection: string[]) => {
        // Complex handler for filtered fields case
        const filteredSelections = newSelection.filter(val => val.startsWith(SYNTHETIC_FILTERED_FIELD));
        const regularSelections = newSelection.filter(val => !val.startsWith(SYNTHETIC_FILTERED_FIELD));
          
        const filteredFieldIdsFromSelection = filteredSelections.map(val => val.replace(SYNTHETIC_FILTERED_FIELD, ''));
        const shouldBeSelected = new Set<string>();
          
        const fieldsInFilters = Array.from(filterFieldIds);
          
        fieldsInFilters.forEach(fieldId => {
          const hasRegular = regularSelections.includes(fieldId);
          const hasFiltered = filteredSelections.includes(`${SYNTHETIC_FILTERED_FIELD}${fieldId}`);
          const wasSelected = selectedFields.includes(fieldId);
            
          if (!wasSelected) {
            if (hasRegular || hasFiltered) {
              shouldBeSelected.add(fieldId);
            }
          } else {
            if (hasRegular && hasFiltered) {
              shouldBeSelected.add(fieldId);
            } else if (!hasRegular && !hasFiltered) {
              // Neither selected - deselect
            } else {
              // Only one version selected - deselect entirely
            }
          }
        });
          
        regularSelections.forEach(fieldId => {
          if (!filterFieldIds.has(fieldId)) {
            shouldBeSelected.add(fieldId);
          }
        });
          
        const finalSelected: string[] = [];
          
        selectedFields.forEach(fieldId => {
          if (shouldBeSelected.has(fieldId)) {
            finalSelected.push(fieldId);
          }
        });
          
        const allSelections = [...regularSelections, ...filteredFieldIdsFromSelection];
        allSelections.forEach(fieldId => {
          if (shouldBeSelected.has(fieldId) && !finalSelected.includes(fieldId)) {
            finalSelected.push(fieldId);
          }
        });
          
        const totalFieldCount = finalSelected.length;
        if (totalFieldCount <= MAXIMUM_FIELD_COUNT) {
          setSelectedFields(finalSelected);
        } else {
          toast({
            title: L.admin_panel.reports.field_limit_reached,
            description: L.admin_panel.reports.field_limit_warning,
            variant: "error"
          });
        }
      }
      : (newSelection: string[]) => {
        // Simple handler for non-filtered fields case
        const filteredSelections = newSelection.filter(val => val.startsWith(SYNTHETIC_FILTERED_FIELD));
        const regularSelections = newSelection.filter(val => !val.startsWith(SYNTHETIC_FILTERED_FIELD));
          
        const filteredFieldIds = filteredSelections.map(val => val.replace(SYNTHETIC_FILTERED_FIELD, ''));
        const allSelected = Array.from(new Set([...filteredFieldIds, ...regularSelections]));
        const totalFieldCount = allSelected.length;

        if (totalFieldCount <= MAXIMUM_FIELD_COUNT) {
          setSelectedFields(allSelected);
        } else {
          toast({
            title: L.admin_panel.reports.field_limit_reached,
            description: L.admin_panel.reports.field_limit_warning,
            variant: "error"
          });
        }
      };

    return {
      key: hasFilteredFields ? "with-filtered-fields" : "without-filtered-fields",
      title: hasFilteredFields ? L.admin_panel.reports.configure_report : L.admin_panel.reports.add_fields,
      options: fieldOptions,
      selectedValues: selectedValuesForOptionSelector,
      onSelectionChange: handleSelectionChange,
      searchPlaceholder: L.admin_panel.reports.search_fields,
      showSelectAllControls: false,
      uncategorizedLabel: L.admin_panel.reports.general_fields,
      customCounterLabel: (
        <Tooltip text={L.admin_panel.reports.select_max} delayDuration={600}>
          <ProgressBadge
            current={selectedFields.length}
            max={MAXIMUM_FIELD_COUNT}
            warningLabel={L.admin_panel.reports.fields_selected}
            defaultLabel={L.admin_panel.reports.fields_selected}
            maxLabel={L.admin_panel.reports.max_fields_selected}
            warningThreshold={0.8}
          />
        </Tooltip>
      ),
      className: "h-full",
      ...(hasFilteredFields && {
        categoryOrder: [SYNTHETIC_FILTERED_FIELD],
        categoryDescriptions: {
          [SYNTHETIC_FILTERED_FIELD]: (
            <span className="text-xs font-medium">Fields from your active filters</span>
          )
        },
        categoryIcons: {
          [SYNTHETIC_FILTERED_FIELD]: (
            <Icon name="SparklesIcon" type="solid" className="w-4 h-4 text-purple-800" />
          )
        }
      }),
      virtualized: true,
    };
  }, [filterFieldIds, fieldOptions, selectedValuesForOptionSelector, selectedFields, toast, L]);

  if (isFieldsLoading) {
    return (
      <div className="p-4">
        <div className="text-center">{L.admin_panel.reports.loading_fields}</div>
      </div>
    );
  }

  const optionSelectorProps = getOptionSelectorProps();
  const { key, ...restProps } = optionSelectorProps;

  return (
    <div className="h-full flex flex-col min-h-0">
      <OptionSelector
        key={key} 
        {...restProps}
      />
    </div>
  );
};
